import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import SearchableAssetResults from '../../ui/inputs/SearchableAssetResults';
import { BanknoteArrowUp, X } from 'lucide-react';
import { useToast } from '../../ui/toast/ToastContext';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import { camelizeKeys } from '../../../utils/camelizeKeys';

const getAssetTitle = (asset) => {
  return asset?.displayName || asset?.name || asset?.alias || asset?.ticker || asset?.symbolId || '--';
};

const getAssetSubtitle = (asset) => {
  const parts = [asset?.ticker, asset?.symbolId, asset?.currency, asset?.exchange].filter(Boolean);

  return parts.length > 0 ? parts.join(' • ') : 'Ativo internacional';
};

const getSearchInputValue = (asset) => {
  return asset?.name || asset?.displayName || asset?.alias || asset?.ticker || asset?.symbolId || '';
};

const AddOffshoreWalletAssetModal = ({ onClose, onSuccess, assetToReplace = null, mode = 'add' }) => {
  const { addToast } = useToast();

  const isReplaceMode = mode === 'replace';

  const title = isReplaceMode ? 'Substituir Ativo Internacional' : 'Buscar Ativo Internacional';

  const submitText = isReplaceMode ? 'SUBSTITUIR ATIVO' : 'ADICIONAR À CARTEIRA';

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searching, setSearching] = useState(false);

  const currentAssetName = useMemo(() => {
    if (!assetToReplace) return null;

    return (
      assetToReplace.assetDisplayName ||
      assetToReplace.assetName ||
      assetToReplace.displayName ||
      assetToReplace.name ||
      assetToReplace.assetTicker ||
      assetToReplace.ticker ||
      assetToReplace.assetSymbolId ||
      assetToReplace.symbolId ||
      null
    );
  }, [assetToReplace]);

  useEffect(() => {
    if (searchTerm.length < 2 || selectedAsset) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    const controller = new AbortController();

    setSearching(true);

    fetch(
      `/app/analyst/wallet_configs/offshore_wallet_assets/search?q=${encodeURIComponent(
        searchTerm
      )}&type=OffshoreSymbol`,
      { signal: controller.signal }
    )
      .then((res) => res.json())
      .then((data) => {
        setSearchResults(camelizeKeys(data));
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.warn('Busca offshore ignorada:', err);
        }
      })
      .finally(() => {
        setSearching(false);
      });

    return () => controller.abort();
  }, [searchTerm, selectedAsset]);

  const handleSelectResult = (asset) => {
    setSelectedAsset(asset);
    setSearchTerm(getSearchInputValue(asset));
    setSearchResults([]);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedAsset(null);
    setSearchResults([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedAsset) {
      addToast({
        type: 'error',
        message: 'Por favor, busque e selecione um ativo internacional na lista.',
      });

      return;
    }

    onSuccess({
      assetType: 'OffshoreSymbol',
      assetId: selectedAsset.id,

      assetName: selectedAsset.name,
      assetDisplayName: selectedAsset.displayName || selectedAsset.name,
      assetAlias: selectedAsset.alias,

      assetTicker: selectedAsset.ticker,
      assetSymbolId: selectedAsset.symbolId,
      assetSymbolType: selectedAsset.symbolType,

      assetCurrency: selectedAsset.currency,
      assetExchange: selectedAsset.exchange,
      assetCountry: selectedAsset.country,
      assetProvider: selectedAsset.provider,

      assetCategory: selectedAsset.category,
      assetStrategy: selectedAsset.strategy,
      assetSector: selectedAsset.sector,
      assetKind: selectedAsset.kind,
      assetSubtype: selectedAsset.subtype,
      assetStatus: selectedAsset.status,

      allocationPercentage: 0,
      _destroy: false,
    });
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <BanknoteArrowUp size={24} className="text-tealMedium" strokeWidth={1.25} />
            {title}
          </div>

          <button type="button" onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {isReplaceMode && currentAssetName && (
          <div className="bg-blueLight rounded-xl px-4 py-3 mb-5 text-blueDarker">
            <span className="font-normal">Substituindo:</span> <span className="font-semibold">{currentAssetName}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 relative">
          <SearchableAssetResults
            name="searchTerm"
            label="Nome, ticker ou symbol ID"
            placeholder="Procurar ativo internacional..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedAsset(null);
            }}
            onClear={handleClearSearch}
            autoFocus
            results={searching ? [] : searchResults}
            onSelectResult={handleSelectResult}
            resultsMaxHeightClassName="max-h-64"
            renderResult={(asset) => (
              <>
                <span className="font-semibold block">{getAssetTitle(asset)}</span>

                <span className="text-xs text-blueSteel block mt-0.5">{getAssetSubtitle(asset)}</span>

                {asset.alias && asset.alias !== getAssetTitle(asset) && (
                  <span className="text-xs text-blueSteel italic block mt-0.5">{asset.alias}</span>
                )}
              </>
            )}
          >
            {searching && searchTerm.length >= 2 && !selectedAsset && (
              <div className="absolute z-50 w-full mt-1 border border-grayLight rounded-md bg-white shadow-lg px-4 py-3 text-blueSteel">
                Buscando...
              </div>
            )}

            {!searching && searchTerm.length >= 2 && searchResults.length === 0 && !selectedAsset && (
              <div className="text-sm text-blueSteel mt-2">Nenhum ativo internacional encontrado.</div>
            )}
          </SearchableAssetResults>

          {selectedAsset && (
            <div className="bg-blueLight rounded-xl px-4 py-3 text-blueDarker">
              <div className="font-semibold">{getAssetTitle(selectedAsset)}</div>

              <div className="text-sm text-blueSteel mt-1">{getAssetSubtitle(selectedAsset)}</div>
            </div>
          )}

          <div className="flex justify-end items-center mt-4">
            <div className="flex justify-between gap-2">
              <SecondaryButton type="button" onClick={onClose}>
                CANCELAR
              </SecondaryButton>

              <Button type="submit">{submitText}</Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddOffshoreWalletAssetModal;
